import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const UncoveringHopeContainer = styled.div`
  text-align: center;
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  overflow: visible;
  padding-bottom: 50px;
  box-sizing: border-box;
  ${(props) => props.isDragging && 'touch-action: none;'}
`;

const Title = styled.h1`
  font-size: 2em;
  margin-bottom: 20px;
  color: #333;
  font-family: 'Cormorant Garamond', serif;
  font-weight: 300;
`;

const Poem = styled.div`
  position: relative;
  z-index: 1;
  font-size: 1.2em;
  line-height: 1.6;
  width: 80%;
  max-width: 600px;
  color: #333;
  margin-bottom: 30px;
  font-family: 'Cormorant Garamond', serif;
`;

const PhrasesContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0; 
  left: 0; 
  pointer-events: none; 
  font-family: 'Cormorant Garamond', serif;
`;

const Phrase = styled.div`
    position: absolute;
    background-color: rgba(200, 200, 200, 0.8);
    padding: 15px 25px;
    border-radius: 5px;
    cursor: grab;
    user-select: none;
    font-size: 1.1em;
    z-index: 2;
    transition: transform 10s ease-in-out;
    top: ${(props) => props.top};
    left: ${(props) => props.left};
    transform: translate(
    ${(props) => props.x}px,
    ${(props) => props.y}px
    );
    margin: 10px;
    pointer-events: auto;
    touch-action: none; /* Add this to prevent scroll on touch devices */

    @media (max-width: 768px) {
        max-width: 40%; 
        font-size: 0.9em;
        padding: 10px 15px;
    }

    &.dragging {
        cursor: grabbing;
        transition: none;
        touch-action: none;
    }
`;

function UncoveringHope() {
  const poem = `As we talked about the future my friend told me, 
  “That dipshits going gentle into that good night.”
  I looked around and thought,
  “Who are we to rage against the dying light?
  The caboose careens towards - who cares?”
  We laugh in the back.
  Going somewhere someone else surmises.
  The conductor\'s cronies are ripping up the dead men\'s train tracks,
  Laid hundreds of years ago.
  I watch passengers fly off, too far away for me to grasp,
  And I wonder are the tracks even there?
  I look out from the caboose:
  Empty land stretches as far as the eye can see.
  There\'s a rumor on the train of an oasis,
  Somewhere where the tracks are smooth,
  And the train ride peaceful,
  And the train engine so powerful that there\'s a seat for everyone.
  No one can decide where it is,
  But it certainly isn\'t this direction.
  My friend wants to build the tracks.
  Any direction is better than this one,
  But no man lays tracks alone.
  I guess I can\'t be a dipshit anymore.`;

  const phrasesData = [
    { text: "Brewing Coffee", top: "10%", left: "35%" },
    { text: "Commuting", top: "10%", left: "55%" },
    { text: "Making Dinner", top: "70%", left: "60%" },
    { text: "Laundry", top: "40%", left: "35%" },
    { text: "Washing the Car", top: "50%", left: "30%" },
    { text: "Mowing the Lawn", top: "5%", left: "40%" },
    { text: "Cleaning the Fridge", top: "75%", left: "40%" },
    { text: "Dusting Cabinets", top: "80%", left: "50%" },
    { text: "Watering the Flowers", top: "90%", left: "40%" },
    { text: "Getting Gas", top: "15%", left: "50%" },
    { text: "Oil Change", top: "50%", left: "50%" },
    { text: "Vacuuming Living Room", top: "20%", left: "50%" },
    { text: "Clearing out Gutters", top: "30%", left: "45%" },
    { text: "Unclogging the Sink", top: "18%", left: "30%" },
    { text: "Washing Shower Curtain Liner", top: "25%", left: "45%" },
    { text: "Defrosting the Freezer", top: "35%", left: "40%" },
    { text: "Replacing Air Filters", top: "45%", left: "45%" },
    { text: "Organizing the Spice Rack", top: "55%", left: "35%" },
    { text: "Washing Reuasble Grocery Bags", top: "65%", left: "30%" },
  ];

  const isMobile = () => {
    return window.innerWidth <= 768;
  };

  const [phrasePositions, setPhrasePositions] = useState(
    phrasesData.map(() => ({ x: 0, y: 0, dragging: false }))
  );

  const [draggedIndex, setDraggedIndex] = useState(null);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (isMobile()) {
      setPhrasePositions(
        phrasesData.map((phrase, index) => {
          // Generate random positions within visible screen area
          const randomLeft = Math.floor(Math.random() * 70) + 5; // 5% to 75% of screen width
          
          return { 
            ...phrasePositions[index], 
            mobilePosition: {
              ...phrase,
              left: `${randomLeft}%` 
            }
          };
        })
      );
    }
  }, []); // Run once on component mount

const handleStart = (event, index) => {
    setDraggedIndex(index);
    const touch = event.touches ? event.touches[0] : event;
    dragStartPos.current = { x: touch.clientX, y: touch.clientY };
    setPhrasePositions((prevPositions) =>
      prevPositions.map((pos, i) =>
        i === index ? { ...pos, dragging: true } : pos
      )
    );
    setIsDragging(true);
    
    // Ensure preventDefault works by stopping propagation too
    event.stopPropagation();
    event.preventDefault();
  };
  
  // Update handleMove to be more aggressive about preventing scroll
  const handleMove = (event) => {
    if (draggedIndex === null) return;
    
    // Prevent scroll more aggressively
    event.stopPropagation();
    event.preventDefault();
    
    const touch = event.touches ? event.touches[0] : event;
    const deltaX = touch.clientX - dragStartPos.current.x;
    const deltaY = touch.clientY - dragStartPos.current.y;
  
    setPhrasePositions((prevPositions) =>
      prevPositions.map((pos, i) =>
        i === draggedIndex
          ? { ...pos, x: pos.x + deltaX, y: pos.y + deltaY }
          : pos
      )
    );
    dragStartPos.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleEnd = (event) => {
    if (draggedIndex === null) return;

    setPhrasePositions((prevPositions) =>
      prevPositions.map((pos, i) =>
        i === draggedIndex ? { x: 0, y: 0, dragging: false } : pos
      )
    );

    setDraggedIndex(null);
    setIsDragging(false); // Update isDragging state
    if (event) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    const handleMoveEvent = (event) => {
      handleMove(event);
    };
  
    window.addEventListener('mousemove', handleMoveEvent);
    window.addEventListener('touchmove', handleMoveEvent, { passive: false }); // Add passive: false
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchend', handleEnd);
  
    return () => {
      window.removeEventListener('mousemove', handleMoveEvent);
      window.removeEventListener('touchmove', handleMoveEvent);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [draggedIndex]);

  return (
    <UncoveringHopeContainer isDragging={isDragging}>
      <Title>Uncovering Hope</Title>
      <Poem>{poem.split('\n').map((line, index) => <p key={index}>{line}</p>)}</Poem>
      <PhrasesContainer>
        {phrasesData.map((phrase, index) => {
          
          const position = isMobile() && phrasePositions[index]?.mobilePosition 
            ? phrasePositions[index].mobilePosition 
            : phrase;
            
          return (
            <Phrase
              key={index}
              top={position.top}
              left={position.left}
              x={phrasePositions[index].x}
              y={phrasePositions[index].y}
              className={phrasePositions[index].dragging ? 'dragging' : ''}
              onMouseDown={(event) => handleStart(event, index)}
              onTouchStart={(event) => handleStart(event, index)}
            >
              {phrase.text}
            </Phrase>
          );
        })}
      </PhrasesContainer>
    </UncoveringHopeContainer>
  );
}

export default UncoveringHope;